import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { issueQueryOptions, issuePeersQueryOptions } from '@/features/ipo/api/queries'
import type { RouterContext } from '@/routes/layouts/root'

/**
 * The `/ipo/$key` loader.
 *
 * MAIN: the issue itself — the header (the company name) and every section
 * below read it, and nothing on the page can draw without it — awaited.
 *
 * SECONDARY, started and not awaited: the sector's peers. `IpoPeersCard`
 * already carries its own loading/failed/retry state
 * (`state={peers.isError ? 'failed' : …}` in `IpoScreen.tsx`), so it stays a
 * plain `useIssuePeers` read, unchanged by this conversion. Only on a real
 * navigation (`!preload`), not on hover — a member's pointer resting on an
 * issue row in `PublicIssuesScreen`'s table must not fire this on its own;
 * see docs/route-loading.md, "Secondary reads".
 *
 * Path-keyed, not search-keyed (this route has no search schema), so unlike
 * the family's tables the screen moves to a suspense read
 * (`useSuspenseIssue`) — see the "Main vs secondary" section of
 * docs/route-loading.md.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own — this screen carries no test file today, but the same
 * constraint on what a loader may import (api/ modules only, never a
 * component module) still applies.
 */
export async function loadIpo({
  context: { queryClient },
  params,
  preload,
}: {
  context: RouterContext
  params: { key: string }
  preload: boolean
}): Promise<void> {
  if (!preload) prefetchRouteData(queryClient, issuePeersQueryOptions(params.key))
  await ensureRouteData(queryClient, issueQueryOptions(params.key))
}
